import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import PasswordStrengthMeter, { scorePassword, MIN_PASSWORD_SCORE } from '../components/PasswordStrengthMeter';
import PasswordInput from '../components/PasswordInput';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';
import PasswordRequirements from '../components/PasswordRequirements';
import useAppVersion from '../hooks/useAppVersion';
import { resolveDisplayVersion } from '../utils/appVersion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  'Employee Self-Service Portal',
  'Leave & Attendance Management',
  'Performance Stack Ranking',
  'Training & Certification',
  'Payroll & Asset Management',
  'Real-time Analytics & Reports',
];

const Login = ({ setUser }) => {
  const appVersion = useAppVersion();
  // Reconcile the shipped bundle's fallback with the DB-published version so
  // a lagging DB can't downgrade the login footer (and so we never flash
  // fallback → older DB value on refresh).
  const { version: displayVersion } = resolveDisplayVersion(
    appVersion?.version,
    appVersion?.publishedAt,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Forgot-password flow state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success(response.data.message);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(response.data.message);
      setStep('forgot-reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    const strength = scorePassword(newPassword, [forgotEmail]);
    if ((strength?.score ?? 0) < MIN_PASSWORD_SCORE) {
      toast.error('Please choose a stronger password (at least "Fair").');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    if (forgotOtp.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, {
        email: forgotEmail,
        otp: forgotOtp,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success('Password reset successfully. Please log in.');
      // Reset state and go back to login screen
      setForgotEmail(''); setForgotOtp(''); setNewPassword(''); setConfirmPassword('');
      setEmail(''); setPassword('');
      setStep('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left Side - Branding Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1d2e] flex-col px-8 xl:px-12 py-8 relative overflow-hidden">
        {/* Top-left Logo */}
        <div className="flex items-center gap-3">
          <img src="/kineserve-logo.svg" alt="KineServe" className="h-16 w-16 xl:h-20 xl:w-20 shrink-0" />
          <span className="text-gray-400 text-sm font-medium tracking-wider leading-tight">AI-Powered<br />HRMS</span>
        </div>

        {/* Center Content */}
        <div className="flex-1 flex flex-col items-center justify-center text-center min-h-0 py-8">
          <h2 className="text-3xl xl:text-4xl font-bold text-white mb-3 tracking-tight" data-testid="branding-title">
            KineServe
          </h2>
          <p className="text-gray-400 text-base xl:text-lg leading-relaxed mb-8 max-w-md mx-auto">
            Enterprise-grade AI-powered HRMS platform built for modern workforce management.
          </p>
          <div className="space-y-3 inline-flex flex-col items-start">
            {FEATURES.map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <span className="text-gray-300 text-sm xl:text-base">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Card */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-4 sm:px-6 py-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          {step === 'login' ? (
            <>
              <div className="flex justify-center mb-6">
                <img src="/gbt-logo.png" alt="Grey Bull Technologies" className="h-16 sm:h-20 w-auto" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2" data-testid="login-title">
                GBT Employee Self Service Portal
              </h1>
              <p className="text-gray-500 text-center mb-6 text-sm sm:text-base">Sign in to access your account</p>

              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-900">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-lg border-gray-300 bg-gray-50"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-900">Password</Label>
                  <PasswordInput
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-lg border-gray-300 bg-gray-50"
                    data-testid="password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg text-base"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Sending OTP...' : 'Continue'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setStep('forgot'); }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </form>
            </>
          ) : step === 'otp' ? (
            <>
              <div className="flex justify-center mb-6">
                <img src="/gbt-logo.png" alt="Grey Bull Technologies" className="h-16 sm:h-20 w-auto" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2" data-testid="otp-title">
                GBT Employee Self Service Portal
              </h1>
              <p className="text-gray-500 text-center mb-6 text-sm sm:text-base">
                Enter the OTP sent to <span className="font-medium text-gray-700">{email}</span>
              </p>

              <form onSubmit={handleVerifyOTP} className="space-y-5" data-testid="otp-form">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      <InputOTPSlot index={1} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      <InputOTPSlot index={2} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      <InputOTPSlot index={3} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      <InputOTPSlot index={4} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      <InputOTPSlot index={5} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg text-base"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-button"
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setOtp(''); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-900"
                  data-testid="back-to-login-button"
                >
                  Back to Login
                </button>
              </form>
            </>
          ) : step === 'forgot' ? (
            <>
              <div className="flex justify-center mb-6">
                <img src="/gbt-logo.png" alt="Grey Bull Technologies" className="h-16 sm:h-20 w-auto" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2" data-testid="forgot-title">
                Forgot Password
              </h1>
              <p className="text-gray-500 text-center mb-6 text-sm sm:text-base">
                Enter your registered email and we'll send you a 6-digit OTP.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-sm font-semibold text-gray-900">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="name@company.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="h-12 rounded-lg border-gray-300 bg-gray-50"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg text-base"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </Button>
                <button
                  type="button"
                  onClick={() => setStep('login')}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-900"
                  data-testid="forgot-back-to-login-button"
                >
                  <ArrowLeft className="inline w-3 h-3 mr-1" /> Back to Login
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-6">
                <img src="/gbt-logo.png" alt="Grey Bull Technologies" className="h-16 sm:h-20 w-auto" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2" data-testid="forgot-reset-title">
                Reset Your Password
              </h1>
              <p className="text-gray-500 text-center mb-6 text-sm sm:text-base">
                OTP sent to <span className="font-medium text-gray-700">{forgotEmail}</span>. Enter it below along with your new password.
              </p>
              <form onSubmit={handleForgotReset} className="space-y-4" data-testid="forgot-reset-form">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={forgotOtp} onChange={setForgotOtp} data-testid="forgot-otp-input">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="w-10 h-12 sm:w-12 sm:h-14 text-lg sm:text-xl" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-pass" className="text-sm font-semibold text-gray-900">New Password</Label>
                  <PasswordInput
                    id="new-pass"
                    placeholder="Minimum 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                    className="h-12 rounded-lg border-gray-300 bg-gray-50"
                    data-testid="forgot-new-password-input"
                  />
                  <PasswordStrengthMeter password={newPassword} userInputs={[forgotEmail]} />
                  <PasswordRequirements password={newPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pass" className="text-sm font-semibold text-gray-900">Confirm New Password</Label>
                  <PasswordInput
                    id="confirm-pass"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    className="h-12 rounded-lg border-gray-300 bg-gray-50"
                    data-testid="forgot-confirm-password-input"
                  />
                  <PasswordMatchIndicator password={newPassword} confirm={confirmPassword} />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg text-base"
                  disabled={loading || forgotOtp.length !== 6}
                  data-testid="forgot-reset-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('forgot'); setForgotOtp(''); setNewPassword(''); setConfirmPassword(''); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-900"
                  data-testid="forgot-reset-back-button"
                >
                  Back
                </button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-xs text-gray-400 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-center px-4">
          <span data-testid="app-version-footer">
            {appVersion?.loading ? (
              <span
                data-testid="app-version-skeleton"
                className="inline-block h-2 w-12 rounded-full bg-gray-200 animate-pulse align-middle"
              />
            ) : (
              `V${displayVersion}`
            )}
          </span>
          <span>&copy; {new Date().getFullYear()} Grey Bull Technologies. All rights reserved.</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
